import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, ENDPOINTS } from '../config';

export default function SourceAutocomplete({ 
  value, 
  onChange, 
  onSourceSelect, 
  placeholder = "Type to search or create a source...",
  className = "form-control"
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Update input value when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Debounced search function
  const searchSources = async (query) => {
    if (!query || query.trim().length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.RECRUITER_SOURCES_SEARCH(query.trim())}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to search sources');
      }

      setSuggestions(data.data || []);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (err) {
      setError(err.message);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Call the onChange prop if provided
    if (onChange) {
      onChange(newValue);
    }

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounce
    debounceRef.current = setTimeout(() => {
      searchSources(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setInputValue(suggestion.source_name);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    
    // Call the callbacks
    if (onChange) {
      onChange(suggestion.source_name);
    }
    if (onSourceSelect) {
      onSourceSelect(suggestion);
    }
  };

  // Handle creating a new source
  const handleCreateSource = async (sourceName) => {
    if (!sourceName || !sourceName.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.RECRUITER_SOURCES}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_name: sourceName.trim()
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 409) {
          // Source already exists, treat as selection
          if (data.existing_source_id) {
            const existingSource = {
              source_id: data.existing_source_id,
              source_name: sourceName.trim()
            };
            handleSuggestionSelect(existingSource);
            return;
          }
        }
        throw new Error(data.error || 'Failed to create source');
      }

      const newSource = data.data;
      handleSuggestionSelect(newSource);
      setShowSuggestions(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Create new source if Enter is pressed and no suggestions shown
        handleCreateSource(inputValue);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else if (selectedIndex === suggestions.length) {
          // Create new source option
          handleCreateSource(inputValue);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Check if input value matches any suggestion exactly
  const exactMatch = suggestions.find(s => 
    s.source_name.toLowerCase() === inputValue.toLowerCase()
  );

  // Show "Create new" option if no exact match and input has value
  const showCreateOption = inputValue.trim() && !exactMatch && !loading;

  return (
    <div className="position-relative">
      <input
        ref={inputRef}
        type="text"
        className={className}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      
      {loading && (
        <div className="position-absolute end-0 top-50 translate-middle-y me-2">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {showSuggestions && (suggestions.length > 0 || showCreateOption) && (
        <div
          ref={suggestionsRef}
          className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
          style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.source_id}
              className={`px-3 py-2 cursor-pointer border-bottom ${
                index === selectedIndex ? 'bg-primary text-white' : 'hover-bg-light'
              }`}
              onClick={() => handleSuggestionSelect(suggestion)}
              style={{ cursor: 'pointer' }}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-search me-2 text-muted"></i>
                <span>{suggestion.source_name}</span>
              </div>
            </div>
          ))}
          
          {showCreateOption && (
            <div
              className={`px-3 py-2 cursor-pointer ${
                selectedIndex === suggestions.length ? 'bg-primary text-white' : 'hover-bg-light'
              }`}
              onClick={() => handleCreateSource(inputValue)}
              style={{ cursor: 'pointer' }}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-plus-circle me-2 text-success"></i>
                <span>Create "<strong>{inputValue.trim()}</strong>"</span>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-danger small mt-1">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {error}
        </div>
      )}
    </div>
  );
} 